import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { Task } from "../../types";

export function useMoveListTasks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      fromListId,
      toListId,
    }: {
      fromListId: string;
      toListId: string;
    }) => {
      // Only the List's Owner may do this in one shot (ADR-0003) — going
      // through the RPC makes that refusal explicit and all-or-nothing,
      // rather than a plain bulk UPDATE silently narrowing itself to just
      // the caller's own Tasks under row-level security.
      const { error } = await supabase.rpc("move_list_tasks", {
        p_from_list_id: fromListId,
        p_to_list_id: toListId,
      });
      if (error) throw error;
    },

    onMutate: async ({ fromListId, toListId }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.tasks });
      const prevTasks = queryClient.getQueryData<Task[]>(queryKeys.tasks);

      // Left alone when the cache has not loaded: writing `[]` into it would
      // flip useTasks out of its pending state and drop the app's loading gate,
      // and onError's snapshot guard could not put it back.
      queryClient.setQueryData<Task[]>(queryKeys.tasks, (old) =>
        old
          ? old.map((t) =>
              t.list_id === fromListId ? { ...t, list_id: toListId } : t
            )
          : old
      );

      return { prevTasks };
    },

    onError: (err, _, context) => {
      if (context?.prevTasks)
        queryClient.setQueryData<Task[]>(queryKeys.tasks, context.prevTasks);
      Sentry.captureException(err, { extra: { mutation: "useMoveListTasks" } });
      toast.error("Failed to move tasks. Please try again.");
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    },
  });
}
